import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UseraccountserviceService } from './useraccountservice.service';
import { ToastrService } from 'ngx-toastr';

interface AdminType {
  value: string;
  label: string;
}

interface UserAccount {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  otherNames: string;
  adminType: string;
}

@Component({
  selector: 'app-useraccounts',
  templateUrl: './useraccounts.component.html',
  styleUrls: ['./useraccounts.component.scss']
})
export class UseraccountsComponent implements OnInit {
  
  userAccountForm: FormGroup;
  isSubmitting = false;
  submitSuccess = false;
  submitError = false;
  errorMessage = '';
  successMessage = '';

  adminTypes: AdminType[] = [
    { value: 'SYSADMIN', label: 'System Administrator' },
    { value: 'ADMIN', label: 'Administrator' },
    { value: 'MANAGEMENT', label: 'Management' },
    { value: 'REGISTRAR', label: 'Registrar' },
    { value: 'HOD', label: 'Head of Department' },
    { value: 'DEAN', label: 'Dean' },
    { value: 'LECTURER', label: 'Lecturer' },
    { value: 'EXAM_OFFICER', label: 'Exam Officer' },
    { value: 'ACCOUNTANT', label: 'Accountant' },
    { value: 'ESTMANAGER', label: 'Estate Officer' },
    { value: 'QA', label: 'Quality Assurance' },
    { value: 'LIBRARIAN', label: 'Librarian' },
    { value: 'FOUNDER', label: 'Founder' }

  ];

  constructor(
    private fb: FormBuilder,
    private userAccountService: UseraccountserviceService,
    private toastr: ToastrService // Optional
  ) {
    this.userAccountForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@techbridge\.edu\.gh$/)]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      otherNames: [''],
      adminType: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    if (!this.userAccountService.isAuthenticated()) {
      console.warn('User not authenticated');
     
    }
  }

  onSubmit(): void {
    if (this.userAccountForm.invalid) {
      this.markFormGroupTouched(this.userAccountForm);
      this.toastr?.warning('Please correct the highlighted fields.', 'Validation Error');
      return;
    }

    this.isSubmitting = true;
    this.submitSuccess = false;
    this.submitError = false;
    this.errorMessage = '';

    const payload: UserAccount = this.userAccountForm.value;

    // Use the service to create admin user with Bearer token
    this.userAccountService.createAdminUser(payload).subscribe({
      next: (response: any) => {
        console.log('User account created successfully:', response);
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.successMessage = `User account for ${payload.firstName} ${payload.lastName} created successfully!`;
        
        // Show toastr notification
        this.toastr?.success(
          `Account created for ${payload.firstName} ${payload.lastName}`, 
          'Success!'
        );
        
        this.userAccountForm.reset();
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          this.submitSuccess = false;
        }, 5000);
      },
      error: (error) => {
        console.error('Error creating user account:', error);
        this.isSubmitting = false;
        this.submitError = true;
        
        // Extract error message
        const apiErrorMessage = error.error?.message || error.error?.error || 'Failed to create user account. Please try again.';
        this.errorMessage = apiErrorMessage;
        
        // Show toastr notification
        this.toastr?.error(apiErrorMessage, 'Error', {
          positionClass: 'toast-top-center',
          closeButton: true,
          timeOut: 5000
        });
        
        // Auto-hide error message after 5 seconds
        setTimeout(() => {
          this.submitError = false;
        }, 5000);
      }
    });
  }

  onReset(): void {
    this.userAccountForm.reset();
    this.submitSuccess = false;
    this.submitError = false;
    this.errorMessage = '';
    this.successMessage = '';
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Getter methods for form controls
  get username() { return this.userAccountForm.get('username'); }
  get email() { return this.userAccountForm.get('email'); }
  get firstName() { return this.userAccountForm.get('firstName'); }
  get lastName() { return this.userAccountForm.get('lastName'); }
  get otherNames() { return this.userAccountForm.get('otherNames'); }
  get adminType() { return this.userAccountForm.get('adminType'); }

  // Helper method to check if field has error
  hasError(fieldName: string, errorType: string): boolean {
    const field = this.userAccountForm.get(fieldName);
    return !!(field && field.hasError(errorType) && field.touched);
  }
}